import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { showToast } from '../layout/ToastContainer';

function Toggle({ checked, onChange, label, description }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 0', borderBottom: '1px solid var(--border-dim)' }}>
      <div style={{ flex: 1, paddingRight: 24 }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, marginBottom: 4 }}>{label}</div>
        {description && <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.6 }}>{description}</div>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        style={{
          flexShrink: 0,
          width: 44, height: 24, borderRadius: 12,
          background: checked ? 'var(--accent-orange)' : 'var(--border-dim)',
          border: 'none', cursor: 'pointer', position: 'relative',
          transition: 'background 0.2s'
        }}
        aria-pressed={checked}
      >
        <div style={{
          position: 'absolute', top: 3, left: checked ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
        }} />
      </button>
    </div>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
    } catch {
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(patch) {
    setSaving(true);
    try {
      const { data } = await api.patch('/settings', patch);
      setSettings(data);
      showToast('Settings saved', 'success');
    } catch {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  }

  function toggleBool(key) {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    save({ [key]: !settings[key] });
  }

  function updateEmailEnabled(idx, enabled) {
    const emails = settings.notifyEmails.map((e, i) => i === idx ? { ...e, enabled } : e);
    setSettings(s => ({ ...s, notifyEmails: emails }));
    save({ notifyEmails: emails });
  }

  function removeEmail(idx) {
    const emails = settings.notifyEmails.filter((_, i) => i !== idx);
    setSettings(s => ({ ...s, notifyEmails: emails }));
    save({ notifyEmails: emails });
  }

  function addEmail() {
    if (!newEmail.trim() || !/\S+@\S+\.\S+/.test(newEmail.trim())) {
      showToast('Please enter a valid email address', 'error');
      return;
    }
    const exists = settings.notifyEmails.some(e => e.email === newEmail.trim());
    if (exists) {
      showToast('Email already in the list', 'error');
      return;
    }
    const emails = [...settings.notifyEmails, { email: newEmail.trim(), enabled: true }];
    setSettings(s => ({ ...s, notifyEmails: emails }));
    setNewEmail('');
    save({ notifyEmails: emails });
  }

  function updateWebhook(val) {
    setSettings(s => ({ ...s, slackWebhook: val }));
  }

  function saveWebhook() {
    save({ slackWebhook: settings.slackWebhook });
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 32, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.05em' }}>
            Settings
          </h1>
          <div style={{ color: '#9aa3b0', fontSize: 13, marginTop: 4 }}>
            Configure email notifications, Slack alerts, and other system preferences.
          </div>
        </div>
        <a href="/" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          color: '#9aa3b0', fontSize: 13, textDecoration: 'none',
          border: '1px solid #3e4a5a', borderRadius: 'var(--radius-sm)',
          padding: '6px 12px', transition: 'color 0.15s, border-color 0.15s'
        }}>
          ← Main Page
        </a>
      </div>

      {/* Email Settings */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-dim)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent-orange)' }}>
            Email Settings
          </div>
        </div>
        <div style={{ padding: '0 20px' }}>
          <Toggle
            checked={settings.appendTestResultsToUserEmail}
            onChange={() => toggleBool('appendTestResultsToUserEmail')}
            label="Append Test Results to User Confirmation Email"
            description="When enabled, the user's diagnostic test results (speeds, connection info) are included at the bottom of the confirmation email they receive after submission."
          />
          <Toggle
            checked={settings.emailSignature}
            onChange={() => toggleBool('emailSignature')}
            label="Email Signature"
            description="When enabled, admin reply emails include the sender's name, address, phone, and logo as a formatted signature block."
          />
        </div>
      </div>

      {/* Notification Emails */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-dim)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent-orange)' }}>
            Report Notification Recipients
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>
            When a viewer submits a report, notification emails are sent to all enabled addresses below.
          </div>
        </div>
        <div style={{ padding: '12px 20px' }}>
          {settings.notifyEmails.length === 0 && (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0', fontStyle: 'italic' }}>
              No recipients configured. Add one below.
            </div>
          )}
          {settings.notifyEmails.map((entry, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 0', borderBottom: '1px solid var(--border-dim)'
            }}>
              <button
                onClick={() => updateEmailEnabled(i, !entry.enabled)}
                style={{
                  flexShrink: 0,
                  width: 36, height: 20, borderRadius: 10,
                  background: entry.enabled ? 'var(--accent-orange)' : 'var(--border-dim)',
                  border: 'none', cursor: 'pointer', position: 'relative',
                  transition: 'background 0.2s'
                }}
              >
                <div style={{
                  position: 'absolute', top: 2, left: entry.enabled ? 18 : 2,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.4)'
                }} />
              </button>
              <span style={{
                flex: 1, fontFamily: 'var(--font-mono)', fontSize: 13,
                color: entry.enabled ? 'var(--text-primary)' : 'var(--text-muted)'
              }}>
                {entry.email}
              </span>
              <button
                onClick={() => removeEmail(i)}
                style={{
                  background: 'none', border: '1px solid var(--border-dim)', borderRadius: 4,
                  color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12,
                  padding: '3px 8px', transition: 'color 0.15s, border-color 0.15s'
                }}
              >
                Remove
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <input
              className="input-field"
              type="email"
              placeholder="Add email address..."
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addEmail()}
              style={{ flex: 1 }}
            />
            <button className="btn btn-primary btn-sm" onClick={addEmail} disabled={saving}>
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Admin Display Settings */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-dim)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent-orange)' }}>
            Admin Display
          </div>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 14, marginBottom: 4 }}>Submissions List Auto-Refresh</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 12 }}>How often the Submissions list automatically refreshes to show new entries.</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={settings.refreshInterval ?? 30}
              onChange={e => {
                const val = Number(e.target.value);
                setSettings(s => ({ ...s, refreshInterval: val }));
                save({ refreshInterval: val });
              }}
              style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border-dim)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
                fontSize: 13, padding: '7px 12px', cursor: 'pointer'
              }}
            >
              <option value={0}>Disabled</option>
              <option value={15}>Every 15 seconds</option>
              <option value={30}>Every 30 seconds</option>
              <option value={60}>Every 1 minute</option>
              <option value={120}>Every 2 minutes</option>
              <option value={300}>Every 5 minutes</option>
            </select>
          </div>
        </div>
      </div>

      {/* Slack Settings */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-dim)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent-orange)' }}>
            Slack Notifications
          </div>
        </div>
        <div style={{ padding: '0 20px' }}>
          <Toggle
            checked={settings.slackEnabled}
            onChange={() => toggleBool('slackEnabled')}
            label="Enable Slack Notifications"
            description="When enabled, a Slack message is posted to the configured webhook when a new viewer report is submitted."
          />
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border-dim)' }}>
          <label className="label" style={{ display: 'block', marginBottom: 8 }}>Slack Webhook URL</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="input-field"
              type="text"
              placeholder="https://hooks.slack.com/services/..."
              value={settings.slackWebhook || ''}
              onChange={e => updateWebhook(e.target.value)}
              style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
            <button className="btn btn-primary btn-sm" onClick={saveWebhook} disabled={saving}>
              Save
            </button>
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 6 }}>
            Create an Incoming Webhook in your Slack app configuration and paste the URL here.
          </div>
        </div>
      </div>
    </div>
  );
}
